<script setup>
import { useQuasar } from 'quasar';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import HrIconBtn from './HrIconBtn.vue';

/**
 * Modal side sheet that slides in from the edge of the screen, over the content it belongs to.
 *
 * M3 anchors side sheets to the end edge, which is the default. The start edge suits content that
 * stands in for something normally there, such as a list that sits at the start when there is room.
 *
 * @status recommended
 * @group overlays
 * @useWhen Showing a list or supporting content beside the main content when there is no room to keep it on screen
 * @avoidWhen The content needs a decision before the user goes on — use a dialog
 * @avoidWhen On a phone — use HrBottomSheet, which is easier to reach
 * @related HrBottomSheet
 * @a11y Modal: focus moves into the sheet and is trapped until it closes. The close button, Escape
 *   and the scrim all close it.
 */
defineOptions({ name: 'HrSideSheet' });

const props = defineProps({
  /** Whether the sheet is open. */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Title displayed in the sheet header, and the sheet's accessible name.
   * @example Tasks
   */
  title: {
    type: String,
    required: true,
  },
  /**
   * Which edge the sheet slides in from, mirrored in a right-to-left language.
   * @values end, start
   */
  edge: {
    type: String,
    default: 'end',
    validator: (value) => ['end', 'start'].includes(value),
  },
  /** Accessible name of the close button. Defaults to the translated "Close". */
  closeLabel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** Emitted when the sheet opens or closes. */
  'update:modelValue': null,
});

const $q = useQuasar();
const { t } = useI18n();
const qDialogRef = ref(null);

// Quasar positions dialogs by physical side, so the logical edge is mirrored for right-to-left.
const position = computed(() => {
  const startSide = $q.lang.rtl ? 'right' : 'left';
  const endSide = $q.lang.rtl ? 'left' : 'right';

  return props.edge === 'start' ? startSide : endSide;
});

const effectiveCloseLabel = computed(() => props.closeLabel ?? t('common.buttonClose'));

defineExpose({
  /** Opens the sheet. */
  show: () => qDialogRef.value?.show(),
  /** Closes the sheet. */
  hide: () => qDialogRef.value?.hide(),
});
</script>

<template>
  <!-- Quasar puts these attributes on its role="dialog" element, which the title then names. -->
  <q-dialog
    ref="qDialogRef"
    :model-value="modelValue"
    :aria-label="title"
    :position="position"
    full-height
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- A div, not a section: Quasar lets pointer events through only to a dialog's direct div child,
         so any other element leaves the whole sheet unclickable. -->
    <div class="hr-side-sheet">
      <div class="hr-side-sheet__title-row">
        <span class="hr-title-large">{{ title }}</span>
        <hr-icon-btn
          icon="close"
          :aria-label="effectiveCloseLabel"
          :legacy-props="{ flat: true, round: true }"
          data-e2e="button-side-sheet-close"
          @click="emit('update:modelValue', false)"
        />
      </div>
      <div class="hr-side-sheet__content">
        <!-- @slot Sheet content. -->
        <slot />
      </div>
    </div>
  </q-dialog>
</template>

<style lang="scss">
/* Not scoped: Quasar teleports dialogs outside the component's DOM. */

.hr-side-sheet {
  display: flex;
  flex-direction: column;
  width: var(--app-comp-side-sheet-width);
  max-width: 100vw;
  height: 100%;
  background: white; /* fallback for legacy (no theme) */
}

[data-theme] .hr-side-sheet {
  /* M3 modal side sheets sit at elevation 1, which maps to surface-container-low. */
  background: var(--app-surface-container-low);
  color: var(--app-on-surface);
}

/*
 * The corners away from the edge it is anchored to are rounded, as M3's modal side sheet's are.
 * Physical sides on purpose: Quasar's class names the physical side, already mirrored in script for
 * right-to-left, so the build's RTL conversion must not flip these a second time.
 */
[data-theme] .q-dialog__inner--left .hr-side-sheet {
  /* rtl:ignore */
  border-top-right-radius: var(--app-shape-corner-large);
  /* rtl:ignore */
  border-bottom-right-radius: var(--app-shape-corner-large);
}

[data-theme] .q-dialog__inner--right .hr-side-sheet {
  /* rtl:ignore */
  border-top-left-radius: var(--app-shape-corner-large);
  /* rtl:ignore */
  border-bottom-left-radius: var(--app-shape-corner-large);
}

.hr-side-sheet__title-row {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  padding: 24px 12px 16px 24px;
}

.hr-side-sheet__content {
  min-height: 0;
  overflow-y: auto;
}
</style>
